import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { 
  getFilteredTransactions, 
  createTransaction, 
  updateTransaction, 
  deleteTransaction,
  Transaction,
  TransactionWithDetails,
  TransactionFilters 
} from '../lib/services/TransactionService';

interface UseTransactionsReturn {
  transactions: TransactionWithDetails[];
  loading: boolean;
  error: string | null;
  refreshTransactions: () => Promise<void>;
  addTransaction: (transaction: Transaction) => Promise<{ success: boolean; error?: string }>;
  editTransaction: (id: string, updates: Partial<Transaction>) => Promise<{ success: boolean; error?: string }>;
  removeTransaction: (id: string) => Promise<{ success: boolean; error?: string }>;
}

export const useTransactions = (filters: TransactionFilters = {}): UseTransactionsReturn => {
  const { user } = useAuth();
  const { impersonatedUserId } = useImpersonation();
  const [transactions, setTransactions] = useState<TransactionWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('useTransactions - Récupération des transactions avec filtres:', filters);
      
      const { data, error: fetchError } = await getFilteredTransactions(filters);
      
      if (fetchError) {
        console.error('useTransactions - Erreur lors de la récupération:', fetchError);
        setError(fetchError.message || 'Erreur lors de la récupération des transactions');
        return;
      }

      if (data) {
        console.log('useTransactions - Transactions récupérées:', data.length);
        setTransactions(data);
      } else {
        console.log('useTransactions - Aucune transaction trouvée');
        setTransactions([]);
      }
    } catch (err) {
      console.error('useTransactions - Exception:', err);
      setError(err instanceof Error ? err.message : 'Erreur inattendue');
    } finally {
      setLoading(false);
    }
  };

  const refreshTransactions = async () => {
    await fetchTransactions();
  };

  const addTransaction = async (transaction: Transaction): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('useTransactions - Ajout d\'une transaction:', transaction);
      
      const { data, error: createError } = await createTransaction(transaction);
      
      if (createError) {
        console.error('useTransactions - Erreur lors de la création:', createError);
        return { success: false, error: createError.message || 'Erreur lors de la création' };
      }

      if (data) {
        console.log('useTransactions - Transaction créée avec succès');
        await fetchTransactions();
        window.dispatchEvent(new Event('suivifinance:comptes-changed'));
        return { success: true };
      }

      return { success: false, error: 'Erreur lors de la création' };
    } catch (err) {
      console.error('useTransactions - Exception lors de la création:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Erreur inattendue' };
    }
  };

  const editTransaction = async (id: string, updates: Partial<Transaction>): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('useTransactions - Modification de la transaction:', id, updates);
      
      const { data, error: updateError } = await updateTransaction(id, updates);
      
      if (updateError) {
        console.error('useTransactions - Erreur lors de la modification:', updateError);
        return { success: false, error: updateError.message || 'Erreur lors de la modification' };
      }

      if (data) {
        console.log('useTransactions - Transaction modifiée avec succès');
        await fetchTransactions();
        window.dispatchEvent(new Event('suivifinance:comptes-changed'));
        return { success: true };
      }

      return { success: false, error: 'Erreur lors de la modification' };
    } catch (err) {
      console.error('useTransactions - Exception lors de la modification:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Erreur inattendue' };
    }
  };

  const removeTransaction = async (id: string): Promise<{ success: boolean; error?: string }> => {
    try {
      console.log('useTransactions - Suppression de la transaction:', id);
      
      const { success, error: deleteError } = await deleteTransaction(id);
      
      if (!success) {
        console.error('useTransactions - Erreur lors de la suppression:', deleteError);
        return { success: false, error: deleteError?.message || 'Erreur lors de la suppression' };
      }

      console.log('useTransactions - Transaction supprimée avec succès');
      await fetchTransactions();
      window.dispatchEvent(new Event('suivifinance:comptes-changed'));
      return { success: true };
    } catch (err) {
      console.error('useTransactions - Exception lors de la suppression:', err);
      return { success: false, error: err instanceof Error ? err.message : 'Erreur inattendue' };
    }
  };

  // Charger les transactions au montage du composant et quand les filtres / impersonation changent
  useEffect(() => {
    if (user) {
      fetchTransactions();
    } else {
      setTransactions([]);
      setLoading(false);
    }
  }, [user, impersonatedUserId, JSON.stringify(filters)]); // Utiliser JSON.stringify pour comparer les objets

  return {
    transactions,
    loading,
    error,
    refreshTransactions,
    addTransaction,
    editTransaction,
    removeTransaction
  };
}; 